import React, { useState, useEffect } from 'react';
import {
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

interface ContactCardProps {
	_id: string;
	name: string;
	email: string;
	mobile: string;
	message: string;
	status: number;
	followupStatus: string;
	createdAt?: string;
	updatedAt?: string;
}

interface CreateContactsProps {
	product: ContactCardProps | null;
	onSubmit: (formData: ContactCardProps) => Promise<void>;
	// handleCloseDialog: () => void;
	isDisabled: boolean; // Add the isDisabled prop
}

const CreateContacts: React.FC<CreateContactsProps> = ({ product, onSubmit, isDisabled }) => {
	const [formData, setFormData] = useState<ContactCardProps>({
		_id: '',
		name: '',
		email: '',
		mobile: '',
		message: '',
		status: 0,
		followupStatus: '',
	});

	const [isOtherSelected, setIsOtherSelected] = useState(false);
	const [otherFollowupStatus, setOtherFollowupStatus] = useState('');

	const predefinedOptions = [
		'New',
		'Under Review',
		'Awaiting Supplier',
		'Quotation Sent',
		'Follow-up Pending',
		'Confirmed',
		'Closed',
		'Cancelled',
		'Other',
	];

	useEffect(() => {
		if (product) {
			setFormData(product);
			setIsOtherSelected(!predefinedOptions.includes(product.followupStatus));
			setOtherFollowupStatus(
				!predefinedOptions.includes(product.followupStatus) ? product.followupStatus : ''
			);
		}
	}, [product]);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
	) => {
		const { name, value } = e.target;
		setFormData((prevData) => {
			const updatedData = { ...prevData, [name as string]: value };
			return updatedData;
		});
	};

	const handleFollowupStatusChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value as string;
		setFormData((prevData) => {
			const updatedData = { ...prevData, followupStatus: value };
			return updatedData;
		});
		setIsOtherSelected(value === 'Other');
	};

	const handleOtherFollowupStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setOtherFollowupStatus(value);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const finalFormData = {
				...formData,
				followupStatus: isOtherSelected ? otherFollowupStatus : formData.followupStatus,
			};
			await onSubmit(finalFormData);
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form id="create-contact-form" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid size={{ xs: 12 }} sx={{ marginTop: '10px' }}>
					<TextField
						label="Name"
						name="name"
						value={formData.name}
						onChange={handleChange}
						fullWidth
						size="small"
						disabled={isDisabled}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Email"
						name="email"
						value={formData.email}
						onChange={handleChange}
						fullWidth
						size="small"
						disabled={isDisabled}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Mobile"
						name="mobile"
						value={formData.mobile}
						onChange={handleChange}
						fullWidth
						size="small"
						disabled={isDisabled}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Message"
						name="message"
						value={formData.message}
						onChange={handleChange}
						fullWidth
						size="small"
						disabled={isDisabled}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<FormControl fullWidth size="small">
						<InputLabel>Follow-up Status</InputLabel>
						<Select
							name="followupStatus"
							value={isOtherSelected ? 'Other' : formData.followupStatus}
							onChange={handleFollowupStatusChange}
							label="Follow-up Status"
							disabled={isDisabled}
						>
							{predefinedOptions.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				{isOtherSelected && (
					<Grid size={{ xs: 12 }}>
						<TextField
							label="Other Follow-up Status"
							name="otherFollowupStatus"
							value={otherFollowupStatus}
							onChange={handleOtherFollowupStatusChange}
							fullWidth
							size="small"
							disabled={isDisabled}
						/>
					</Grid>
				)}
			</Grid>
		</form>
	);
};

export default CreateContacts;
